import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../lib/apiClient';

/**
 * Parse token from URL query string (?token=...).
 */
function parseURLToken() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || null;
  } catch {
    return null;
  }
}

/**
 * Magic-link verification landing page — /magic-link?token=...
 * Verifies token via POST /api/v1/aif/auth/magic-link/verify,
 * stores JWT, redirects to /.
 */
export default function MagicLinkLandingPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState('loading'); // loading | success | invalid | expired | used
  const [errorDetail, setErrorDetail] = useState(null);

  useEffect(() => {
    const token = parseURLToken();
    if (!token) {
      setStatus('invalid');
      return;
    }

    (async () => {
      try {
        const res = await apiClient.post('/api/v1/aif/auth/magic-link/verify', { token });
        const data = res?.data;
        if (data?.access_token) {
          localStorage.setItem('aif_token', data.access_token);
        }
        setStatus('success');
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      } catch (err) {
        const code = err?.status;
        if (code === 404) {
          setStatus('invalid');
        } else if (code === 410) {
          setStatus('expired');
        } else if (code === 409) {
          setStatus('used');
        } else {
          setStatus('invalid');
          setErrorDetail(err?.message || 'Unknown error');
        }
      }
    })();
  }, []);

  const messages = {
    loading: {
      icon: null,
      title: t('magicLink.verifying', { defaultValue: 'Verifying your link...' }),
      subtitle: null,
    },
    success: {
      icon: '\u2705',
      title: t('magicLink.success', { defaultValue: 'Login successful!' }),
      subtitle: t('magicLink.redirecting', { defaultValue: 'Redirecting you now...' }),
    },
    invalid: {
      icon: '\u26D4',
      title: t('magicLink.invalid', { defaultValue: 'Invalid login link' }),
      subtitle: t('magicLink.invalidHint', { defaultValue: 'Please request a new one.' }),
    },
    expired: {
      icon: '\u23F0',
      title: t('magicLink.expired', { defaultValue: 'Link expired' }),
      subtitle: t('magicLink.expiredHint', { defaultValue: 'Please request a new one.' }),
    },
    used: {
      icon: '\u2139\uFE0F',
      title: t('magicLink.used', { defaultValue: 'Link already used' }),
      subtitle: t('magicLink.usedHint', { defaultValue: 'You may already be logged in.' }),
    },
  };

  const msg = messages[status];
  const showRetry = status === 'invalid' || status === 'expired' || status === 'used';

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--aif-bg-tertiary)] px-6">
      <div className="text-center space-y-4 max-w-sm" data-testid="magic-link-page">
        {status === 'loading' ? (
          <div className="w-10 h-10 mx-auto border-3 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" data-testid="magic-link-spinner" />
        ) : (
          <div className="text-5xl">{msg.icon}</div>
        )}
        <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]" data-testid="magic-link-title">
          {msg.title}
        </h2>
        {msg.subtitle && (
          <p className="text-sm text-[var(--aif-text-secondary)]" data-testid="magic-link-subtitle">
            {msg.subtitle}
          </p>
        )}
        {showRetry && (
          <a
            href="/signup"
            className="inline-block mt-2 px-6 py-3 rounded-xl bg-[var(--aif-color-primary)] text-white font-medium text-sm"
            data-testid="magic-link-retry"
          >
            {t('magicLink.tryAgain', { defaultValue: 'Try again' })}
          </a>
        )}
      </div>
    </div>
  );
}
